// Pagador do boleto de uma cobrança de acordo de parcelamento (entrada e parcelas).
//
// Regra (Vinícius, 23/09/2026): quando outra pessoa assume a dívida — comprou o
// estabelecimento, por exemplo — os boletos do acordo saem no nome DELA, não no
// do titular da UC. O titular continua no termo como devedor; quem paga é o
// responsável.
//
// Mora em _shared porque três lugares decidem o pagador e precisam dar a mesma
// resposta: a criação do acordo (fatura da entrada), a tela de emissão (Sicoob)
// e a edge function do Asaas. Os dois últimos recalculam o pagador pelo cadastro
// da UC — sem esta regra, o boleto voltava para o titular na hora de emitir.
//
// Sem I/O: roda sob `node --test`, no navegador e no Deno.

export interface EnderecoPagador {
  cep: string;
  logradouro: string;
  numero: string;
  bairro: string;
  cidade: string;
  uf: string;
}

export interface PagadorAcordo {
  nome: string;
  documento: string;
  endereco: EnderecoPagador;
}

const digitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');
const texto = (v: unknown) => String(v ?? '').trim();

/** O que falta no endereço para o banco registrar o boleto. Vazio = completo. */
export function faltasEnderecoPagador(e: Partial<EnderecoPagador> | null | undefined): string[] {
  const faltas: string[] = [];
  if (digitos(e?.cep).length !== 8) faltas.push('CEP');
  if (!texto(e?.logradouro)) faltas.push('rua');
  if (!texto(e?.numero)) faltas.push('número');
  if (!texto(e?.bairro)) faltas.push('bairro');
  if (!texto(e?.cidade)) faltas.push('cidade');
  if (!/^[A-Za-z]{2}$/.test(texto(e?.uf))) faltas.push('UF');
  return faltas;
}

/**
 * O pagador do acordo, a partir de `parcelamentos.termo_dados`. `null` quando
 * os boletos seguem a regra normal (cadastro da UC) — acordo sem responsável,
 * responsável sem a opção marcada, ou dado incompleto.
 *
 * Dado incompleto devolve null de propósito: sem documento o boleto sairia sem
 * pagador, e a regra normal ao menos gera um boleto válido. A tela de criação já
 * barra essa situação antes de gravar.
 */
export function pagadorDoAcordo(termoDados: unknown): PagadorAcordo | null {
  const d = termoDados as { boletoNoResponsavel?: unknown; coobrigado?: any } | null | undefined;
  if (!d || d.boletoNoResponsavel !== true) return null;
  const co = d.coobrigado;
  const documento = digitos(co?.documento);
  if (!texto(co?.nome) || (documento.length !== 11 && documento.length !== 14)) return null;
  const e = co.enderecoPartes || {};
  return {
    nome: texto(co.nome),
    documento,
    endereco: {
      cep: digitos(e.cep),
      logradouro: texto(e.logradouro),
      numero: texto(e.numero),
      bairro: texto(e.bairro),
      cidade: texto(e.cidade),
      uf: texto(e.uf).toUpperCase(),
    },
  };
}

/** Os campos de pagador de `faturas`, prontos para gravar. */
export function camposFaturaDoPagador(p: PagadorAcordo) {
  return {
    nome_cliente_boleto: p.nome,
    cpf_cnpj_cliente_boleto: p.documento,
    end_logradouro: p.endereco.logradouro,
    end_numero: p.endereco.numero,
    end_bairro: p.endereco.bairro,
    end_cep: p.endereco.cep,
    end_cidade: p.endereco.cidade,
    end_uf: p.endereco.uf,
  };
}
